import { ParsedUrlQuery } from 'querystring';
import { removeMostFalsyValues } from '@zoopla/object-remove-falsy-values';
import {
    getBedsParams,
    getPriceFrequencyParam,
    getPriceParams
} from '@zoopla/listing-search-utils';
import { SearchSource, SearchType } from '@zoopla/listing-search-context';
import { getPolyEnc } from './getPolyEnc';

const LIST_PAGE_SIZE = '25';
const MAPS_PAGE_SIZE = '100';

const getPropertySubType = (propertySubTypes: string[]) => {
    return propertySubTypes.join('&property_sub_type=');
};

type SearchPageType = 'for-sale' | 'to-rent' | 'new-homes';

export const updateQueryParams = (
    // eslint-disable-next-line
    filters: any,
    searchType: SearchPageType,
    searchSource: SearchSource,
    sortOrder: string,
    isPropertyTypeFlatsOrHouses: boolean,
    isBungalow: boolean,
    userAlertId: string | null,
    excludeRadius: boolean,
    locationText: string,
    isMaps: boolean,
    polyEnc?: string | string[],
    excludePageSize?: boolean,
    hasHidePoly?: boolean
) => {
    const {
        location,
        radius,
        propertyType,
        propertySubType,
        propertyAdded,
        features,
        keywords,
        availableFrom,
        includeLet,
        includeShared,
        furnishings,
        beds,
        price,
        priceFrequency,
        priceMonthly,
        priceWeekly,
        duration,
        transportType,
        underOfferOrSold,
        chainFree,
        priceReduced,
        shortLets,
        billsIncluded,
        petsAllowed,
        helpToBuy,
        newHomes,
        retirementHome,
        auction,
        isSharedOwnership
    } = filters;
    const bedsParams = getBedsParams(beds);
    const priceParams = getPriceParams(price, priceMonthly, priceWeekly);

    const includeIsAuction =
        (isPropertyTypeFlatsOrHouses && auction !== '') ||
        (!isPropertyTypeFlatsOrHouses && auction === 'false');

    const includeIsRetirementHome =
        (isPropertyTypeFlatsOrHouses && retirementHome !== '') ||
        (!isPropertyTypeFlatsOrHouses && retirementHome === 'false');

    const newHomesValue =
        newHomes === 'exclude'
            ? 'exclude'
            : searchType === SearchType.NewHomes && locationText
            ? 'only'
            : '';

    const pageSize = isMaps ? MAPS_PAGE_SIZE : LIST_PAGE_SIZE;

    // NOTE: The order of these filters matters for SEO purposes
    // Mostly alphabetical with a few exceptions
    const queryParams: ParsedUrlQuery = {
        added: propertyAdded ? propertyAdded : '',
        available_from: availableFrom ? availableFrom : '',
        beds_max: bedsParams.beds_max,
        beds_min: bedsParams.beds_min,
        buyer_incentive: helpToBuy ? 'help_to_buy' : '',
        chain_free: chainFree ? 'true' : '',
        feature: features.join('&feature='),
        include_sold: underOfferOrSold ? 'true' : '',
        is_auction: includeIsAuction ? auction : '',
        is_retirement_home: includeIsRetirementHome ? retirementHome : '',
        is_shared_ownership: !isSharedOwnership ? 'false' : '',
        keywords,
        bills_included: billsIncluded ? billsIncluded : '',
        new_homes: newHomesValue,
        furnished_state:
            furnishings && furnishings !== 'Any' ? furnishings : '',
        include_rented: includeLet ? includeLet : '',
        include_shared_accommodation: includeShared === false ? 'false' : '',
        page_size: excludePageSize ? '' : pageSize,
        pets_allowed: petsAllowed ? petsAllowed : '',
        polyenc: getPolyEnc(polyEnc),
        price_frequency: getPriceFrequencyParam(searchType, priceFrequency),
        price_max: priceParams.price_max,
        price_min: priceParams.price_min,
        property_type:
            propertyType === 'farms_land' || locationText ? propertyType : '',
        property_sub_type: getPropertySubType(propertySubType),
        view_type: isMaps ? 'map' : 'list',
        duration: duration || '',
        q: locationText || (location.value as string),
        orig_q: locationText ? (location.value as string) : '',
        identifier: locationText ? location.id : '',
        category: locationText ? 'residential' : '',
        section: locationText ? searchType : '',
        radius: excludeRadius ? '' : radius || '0',
        rental_term: shortLets ? 'short' : '',
        reduced_price_only: priceReduced === true ? 'true' : '',
        results_sort: sortOrder,
        search_source: searchSource,
        transport_type: transportType || '',
        user_alert_id: userAlertId || '',
        hidePoly: hasHidePoly ? hasHidePoly.toString() : ''
    };

    const result = removeMostFalsyValues(queryParams);
    return result;
};
